import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Activity as ActivityIcon } from "lucide-react";

const ACTION_COLOR = (a) => {
  if (a.includes("deleted") || a.includes("removed") || a.includes("reject")) return "text-destructive";
  if (a.includes("approve") || a.includes("publish") || a.includes("invite")) return "text-emerald-600";
  if (a.includes("submit") || a.includes("commented")) return "text-primary";
  return "text-foreground";
};

export default function ActivityFeed() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/activity?limit=200").then((r) => {
      setItems(r.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((a) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (a.action || "").toLowerCase().includes(s)
      || (a.user_name || "").toLowerCase().includes(s)
      || (a.note || "").toLowerCase().includes(s)
      || (a.entity_type || "").toLowerCase().includes(s);
  });

  // Group by day
  const byDay = filtered.reduce((acc, a) => {
    const day = new Date(a.created_at).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
    (acc[day] = acc[day] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="p-8 space-y-6" data-testid="activity-feed-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">/ WORKSPACE ACTIVITY</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Activity Feed</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every change across your workspace, in chronological order.
          </p>
        </div>
        <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 bg-card dark:bg-card max-w-md flex-1">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by action, user, or entity…"
            data-testid="activity-search"
            className="border-0 focus-visible:ring-0 h-8 p-0"
          />
        </div>
      </div>

      {loading ? (
        <div className="border border-border p-16 text-center text-sm font-mono-plex text-muted-foreground uppercase tracking-widest">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center bg-card dark:bg-card">
          <ActivityIcon className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-4 font-display text-lg">No activity {q ? "matches" : "yet"}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Every create, update, delete, comment, and approval will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(byDay).map(([day, entries]) => (
            <div key={day}>
              <div className="overline text-muted-foreground mb-3">{day}</div>
              <ol className="border-l-2 border-border space-y-3 pl-6">
                {entries.map((a) => (
                  <li key={a.id} className="relative" data-testid={`activity-entry-${a.id}`}>
                    <div className="absolute -left-[27px] top-2 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium">{a.user_name}</span>
                      <span className={`text-sm ${ACTION_COLOR(a.action)}`}>{a.action}</span>
                      <span className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
                        {a.entity_type}
                      </span>
                      <span className="ml-auto text-[10px] font-mono-plex text-muted-foreground uppercase tracking-widest">
                        {new Date(a.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    {a.note && (
                      <div className="text-xs text-muted-foreground italic mt-0.5">&ldquo;{a.note}&rdquo;</div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
